// Import Packages
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSetRecoilState } from 'recoil';

// Import Material UI Elements
import Card from "@material-ui/core/Card";
import CardContent from "@material-ui/core/CardContent";
import CardHeader from "@material-ui/core/CardHeader";
import { makeStyles } from '@material-ui/core/styles';
import IconButton from '@material-ui/core/IconButton';
import CircularProgress from '@material-ui/core/CircularProgress';
import Divider from '@material-ui/core/Divider';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import Button from '@material-ui/core/Button';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { useTheme } from '@material-ui/core/styles';
import Grid from "@material-ui/core/Grid";
import AddBoxIcon from '@material-ui/icons/AddBox';
import Close from "@material-ui/icons/Close";
import CardActions from '@material-ui/core/CardActions';

// Import Components
import Action from '../Action/Action_new_design';
import FieldValuePreview from '../FieldValuePreview/FieldValuePreview';
import { alertState } from '../../../../../recoil-store/recoil-state';

// Import Function Objects | Arrays
import { functionList, actionList } from '../functions/functions';

// Import CSS
import './ObjectExplorer.css';

const useStyles = makeStyles((theme) => ({
    header: {
        color: 'white',
        backgroundColor: 'var(--black-pearl)',
        fontSize: '14px',
        padding: '9px 30px 7px 30px',
        fontFamily: 'Courier !important'
    }
}));
// ,
//     root: {
//         flexGrow: 1,
//     },
//     paper: {
//         height: 140,
//         width: 100,
//     },
//     control: {
//         padding: theme.spacing(2),
//     },
function ObjectExplorer({
    variables,
    setOpenVariable,
    cancel,
    show,
    field,
    variableRender,
    save,
    functions,
    saveNewValue
}) {
    const { t } = useTranslation();
    const theme = useTheme();

    const [loading, setLoading] = useState(true);
    const [actions, setActions] = useState([{}]);
    const [sourceValue, setSourceValue] = useState('');
    const [dataTableValue, setDataTableValue] = useState();
    const [selectedVariable, setSelectedVariable] = useState('');

    const fullWidth = useMediaQuery(theme.breakpoints.up('lg'));
    const label = 'CONTENT.FLOW.OBJECT-EXPLORER.';

    const setAlert = useSetRecoilState(alertState);

    useEffect(() => {
        setTimeout(() => {

            let _actions = [];
            for (const func of functions) {
                let action = JSON.parse(JSON.stringify(actionList[func.name]))
                action.fields = func.fields;
                action.conditions = func.conditions;
                _actions.push(action);
            }
            if (!_actions.length) {
                _actions.push({});
            }
            setActions(_actions);
            setSourceValue(field.value);

            setLoading(false);
        }, 1000)
    }, [])

    // This function is used to update source value.
    useEffect(() => {
        if (field.type === 'string' && selectedVariable && dataTableValue) {
            let v = `##${selectedVariable}[${dataTableValue.rowIndex}][${dataTableValue.colIndex}]##`
            // changed source value
            setSourceValue(v);
            if (actions.length) {
                // Update source values in actions
                let isChanged = false;
                let copyActions = JSON.parse(JSON.stringify(actions));
                for (let i = 0; i < copyActions.length; i++) {
                    if (copyActions[i].fields) {
                        for (const key in copyActions[i].fields) {
                            if (copyActions[i].fields[key].defaultValue) {
                                copyActions[i].fields[key].value = v;
                                isChanged = true;
                            }
                        }
                    }
                }
                if (isChanged) {
                    setActions(copyActions);
                }
            }

        }
    }, [dataTableValue])

    const addAction = () => {
        let actionData = [...actions];
        actionData.push({});
        setActions(actionData)
    }

    const classes = useStyles();

    const checkConditionRequired = (conditions) => {
        for (const c of conditions) {
            if (c.fields.firstValue.value === '') {
                setAlert(alert => ({
                    ...alert,
                    show: true,
                    message: t("CONTENT.CONFIGURATION-FORM.TRIGGER-STATIC.FIELD-PREVIEW.CLICK-TO-PROVIDE-REQUIRED-FIELD"),
                    severity: 'error'
                }))
                return true
            } else if (c.operator === 'empty' || c.operator === 'nempty' || c.condition === 'ping') {
                continue
            } else if (c.fields.secondValue.value === '') {
                setAlert(alert => ({
                    ...alert,
                    show: true,
                    message: t("CONTENT.CONFIGURATION-FORM.TRIGGER-STATIC.FIELD-PREVIEW.CLICK-TO-PROVIDE-REQUIRED-FIELD"),
                    severity: 'error'
                }))
                return true
            }
        }
        return false
    }

    const saveConf = () => {
        let _functions = [];
        let conditionExist = false;
        for (const action of actions) {
            if (!Object.keys(action).length) {
                continue;
            }
            // check required field in conditions.
            if (action.conditions && checkConditionRequired(action.conditions)) {
                return;
            }

            let f = {
                name: `${action.libraryName}-${action.actionName}`,
                fields: {},
                conditions: action.conditions
            }
            const func = functionList[f.name]
            if (!conditionExist && f.conditions && f.conditions.length) {
                conditionExist = true;
            }

            // Check required fields and assign parameters
            for (const p of func.parameters) {
                if (action.fields[p.name] && action.fields[p.name].value) {
                    // f += `${action.fields[p.name].value},`;
                    f.fields[p.name] = { value: action.fields[p.name].value }
                    
                } else if (p.required) {
                    setAlert(alert => ({
                        ...alert,
                        show: true,
                        message: t("CONTENT.CONFIGURATION-FORM.TRIGGER-STATIC.FIELD-PREVIEW.CLICK-TO-PROVIDE-REQUIRED-FIELD"),
                        severity: 'error'
                    }))
                    return
                } else if (p.default) {
                    f.fields[p.name] = { value: p.default };
                }
            }

            // Set defaultValue
            Object.keys(actionList[f.name].fields).forEach((key) => {
                if (action.fields[key]) {
                    f.fields[key] = { value: action.fields[key].value };
                    if(action.fields[key].defaultValue){
                        f.fields[key].defaultValue = true;
                    }
                }
            })

            _functions.push(f);
        }
        if (field.type === 'string' && selectedVariable && dataTableValue) {
            let v = `##${selectedVariable}[${dataTableValue.rowIndex}][${dataTableValue.colIndex}]##`
            saveNewValue(v, selectedVariable);
        }
        if (_functions.length) {
            save(_functions, conditionExist);
        }
    }

    return (
        loading ? (
            <Dialog open={true} maxWidth={'sm'}><DialogActions style={{ padding: '30px' }}><CircularProgress /></DialogActions></Dialog >
        ) : (
            < Dialog open={show} fullWidth={fullWidth} maxWidth={'lg'}
                PaperProps={{
                    style: {
                        backgroundColor: 'transparent',
                        boxShadow: 'none',
                    },
                }}
            >

                <DialogContent className='max-h-600 padding-0' >
                    <Grid container >
                        <Grid item xs={5} className="grid-space-between">
                            <div className='h-100 grid-content left-page'>
                                <div className='custom-modal-header left'>
                                    <div className='font-manrope new-modal-title left-page-title'>
                                        {t(label + 'TITLE')}
                                        {
                                            field.type.includes('string-') &&
                                            <Button className='modal-close-button' onClick={() => cancel()}>
                                                <Close className='modal-close-icon' />
                                            </Button>
                                        }

                                    </div>
                                </div>
                                <div className='left-section-container'>
                                    <FieldValuePreview sourceValue={sourceValue} type={field.type} displayName={field.displayName} variables={variables}
                                        cancel={cancel} saveNewValue={saveNewValue} selectedVariableName={field.selectedVariableName}
                                        dataTableValue={dataTableValue} setDataTableValue={setDataTableValue}
                                        selectedVariable={selectedVariable} setSelectedVariable={setSelectedVariable} />

                                </div>


                            </div>

                        </Grid>
                        {/* <Grid item xs={0.5}>
                            <Divider style={{ marginLeft: '50%' }} orientation={'vertical'} />
                        </Grid> */}


                        <Grid item xs={7} className={field.type.includes('string-') ? 'disable-right-page' : ''}>
                            <div className='h-100 grid-content right-page'>
                                <div className='font-manrope custom-modal-header right'>
                                    <div className='new-modal-title right-page-title'>{
                                        t(label + 'OBJECT-EXPLORER')}
                                        {!field.type.includes('string-') &&
                                            <Button className='modal-close-button' onClick={() => cancel()}>
                                                <Close className='modal-close-icon' />
                                            </Button>
                                        }
                                    </div>
                                    <div className='object-explorer-description'>
                                        {t('CONTENT.CONFIGURATION-FORM.ADVANCED-ACTIONS.ADVANCED-ASSIGN.CONFIGURE-MODAL-DESCRIPTION')}
                                    </div>
                                </div>

                                {/* <div style={{ fontSize: '13px', color: '#8C90A2', fontWeight: 700, marginBottom: '5px' }}>
                                    {t('CONTENT.CONFIGURATION-FORM.ADVANCED-ACTIONS.ADVANCED-ASSIGN.CONFIGURE-MODAL-DESCRIPTION')}
                                </div> */}
                                <div className='right-section-container'>
                                    <Card className='explorer-card'>
                                        <CardHeader className={classes.header} titleTypographyProps={{ variant: '' }} title={field.displayName}
                                        // action={
                                        //     <IconButton style={{ color: '#fcfcfc', marginTop: '8px' }} onClick={addAction}>
                                        //         <AddBoxIcon style={{ marginLeft: '2%' }} />
                                        //     </IconButton>
                                        // }
                                        />
                                        <CardContent className='explorer-card-content' >
                                            {
                                                actions.map((action, index) => <Action field={field} sourceValue={sourceValue} actions={actions}
                                                    setActions={setActions} index={index}
                                                    variables={variables} setOpenVariable={setOpenVariable}
                                                    variableRender={variableRender} />)
                                            }
                                        </CardContent>

                                    </Card>
                                    {!field.type.includes('string-') &&
                                        <div className='rigth-page-footer'>
                                            <Button className='active-save-button object-explorer-save-button' onClick={saveConf} variant="contained" size="medium" >
                                                {t(label + 'SAVE')}
                                            </Button>
                                        </div>
                                    }

                                </div>
                            </div>
                        </Grid>
                    </Grid>
                </DialogContent>
                {/* <DialogActions style={{ justifyContent: 'space-between' }}>
                    <Button
                        onClick={() => cancel()}
                        variant="outlined"
                        style={{ backgroundColor: "#BBBBBB", borderRadius: "2px", color: "#fff", width: "45%", border: "none" }}>
                        {t('CONTENT.MODAL.CANCEL')}
                    </Button>
                    <Button
                        onClick={saveConf}
                        variant="outlined"
                        style={{ backgroundColor: "#46474E", borderRadius: "2px", color: "#fff", width: "45%", border: "none" }}>
                        {t('CONTENT.MODAL.SAVE')}
                    </Button>
                </DialogActions> */}
            </Dialog >
        )
    )
}

export default ObjectExplorer;